import { useMemoAsync } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { $parse } from "@opendash/plugin-parse";
import { useNavigate } from "@opendash/router";
import { AdminToolbar, FixedDescription } from "@opendash/ui";
import { Button, Descriptions, Divider, Flex, Tag } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useState } from "react";
import {
  AssignmentTag,
  AssignUserModal,
  IssueTag,
  PriorityTag,
  ticketDataToFormValues,
  TicketDeleteModal,
  TicketSaveModal,
  TicketStateTag,
  useTicketData,
} from "../../..";
import { UserTag } from "../../../../../_shared";
import {
  Kanban_State,
  Ticket_Kanban_State,
} from "../../../../../js/parsegenerated";
import { ItemLabel } from "../../../../items/components/ItemLabel";
import { ManualModal, useSourceFiles } from "../../../../knowledge";
import { TicketMetaDescription } from "./TicketMetaDescription";
import { TicketOverviewCard } from "./TicketOverviewCard";

export const TicketDescription = observer((props: { onOkUpdate: Function }) => {
  const ticketData = useTicketData();
  const t = useTranslation();
  const navigate = useNavigate();

  const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);
  const [updateModalVisible, setUpdateModalVisible] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [manualModalVisible, setManualModalVisible] = useState<boolean>(false);
  const { sourceFile } = useSourceFiles(ticketData?.source?.id);

  const initialValues = useMemoAsync(
    async () => await ticketDataToFormValues(ticketData),
    [ticketData]
  );

  const onCancelAssign = () => {
    setAssignModalVisible(false);
  };

  const onOkAssign = () => {
    setAssignModalVisible(false);
    props.onOkUpdate();
  };

  const onCancelUpdate = () => {
    setUpdateModalVisible(false);
  };

  const onOkUpdate = () => {
    setUpdateModalVisible(false);
    props.onOkUpdate();
  };

  const onCancelDelete = () => {
    setDeleteModalVisible(false);
  };

  const onOkDelete = () => {
    setDeleteModalVisible(false);
    navigate("/openservice");
  };

  const getDueDateTag = (): JSX.Element => {
    if (!ticketData?.duedate) return;
    if (dayjs(ticketData?.duedate?.get("end")) < dayjs()) {
      return (
        <>
          <Tag color="error">{t("maintenance:ticket.state.overdue")}</Tag>
          <Tag color="error">
            {(ticketData?.duedate?.get("endformat") === "WW in YYYY"
              ? t("maintenance:ticket.state.week") + " "
              : "") +
              dayjs(ticketData?.duedate?.get("end")).format(
                ticketData?.duedate?.get("endformat")
              )}
          </Tag>
        </>
      );
    }
    return (
      <>
        <Tag color="default">{t("maintenance:ticket.state.default")}</Tag>
        <Tag color="processing">
          {t("maintenance:ticket.state.duedate") +
            " " +
            (ticketData?.duedate?.get("endformat") === "WW in YYYY"
              ? t("maintenance:ticket.state.week") + " "
              : "") +
            dayjs(ticketData?.duedate?.get("end")).format(
              ticketData?.duedate?.get("endformat")
            )}
        </Tag>
      </>
    );
  };

  const markAsDone = async () => {
    //Find the state that is the "done"-state
    const finishedStates = await new Parse.Query(Kanban_State)
      .equalTo("isFinished", true)
      .equalTo("enabled", true)
      .find();

    if (finishedStates !== undefined && finishedStates.length > 0) {
      const state = new Ticket_Kanban_State();
      state.set("state", finishedStates[0]);
      state.set("ticket", ticketData.ticket);
      await state.save();
      onOkUpdate();
    }
  };

  return (
    <>
      <AdminToolbar
        children={
          <Flex wrap="wrap" gap="middle">
            {ticketData?.duedate !== undefined && getDueDateTag()}
            <TicketStateTag state={ticketData?.state} />
          </Flex>
        }
        title={ticketData.ticket.get("title")}
        actions={
          <Flex wrap="wrap" gap="middle">
            {sourceFile && (
              <Button
                onClick={() => {
                  setManualModalVisible(true);
                }}
              >
                {t("maintenance:ticket.description.button.show_manuals")}
              </Button>
            )}
            {$parse.ui.getObjectPermission(ticketData.ticket, "update") && (
              <Button
                type="primary"
                onClick={() => {
                  setAssignModalVisible(true);
                }}
              >
                {t("maintenance:ticket.description.buttons.assignuser")}
              </Button>
            )}
            {$parse.ui.getObjectPermission(ticketData.ticket, "update") && (
              <Button
                type="primary"
                onClick={() => {
                  setUpdateModalVisible(true);
                }}
              >
                {t("maintenance:global.button.update_label")}
              </Button>
            )}
            {$parse.ui.getObjectPermission(ticketData.ticket, "update") &&
              ticketData?.state !== undefined &&
              (!ticketData?.state?.get("isFinished") ? (
                <Button
                  onClick={() => {
                    markAsDone();
                  }}
                >
                  {t("maintenance:ticket.description.buttons.markasdone")}
                </Button>
              ) : null)}
            {$parse.ui.getObjectPermission(ticketData.ticket, "delete") && (
              <Button
                onClick={() => {
                  setDeleteModalVisible(true);
                }}
                danger
              >
                {t("maintenance:global.button.delete_label")}
              </Button>
            )}
          </Flex>
        }
      />
      <div className="od-page-main">
        <Flex vertical gap="16px">
          <Divider>Beschreibung</Divider>
          {ticketData.ticket.get("description")}
          <FixedDescription>
            <Divider children={t("maintenance:ticket.description.overview")} />
            <TicketOverviewCard />
            <Divider children={t("maintenance:ticket.description.details")} />
            <Descriptions layout="horizontal" column={4} bordered>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.issuecategory")}
              >
                <IssueTag />
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.priority")}
              >
                <div style={{ width: 200 }}>
                  <PriorityTag asIcon />
                </div>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.assigned")}
                span={2}
              >
                <AssignmentTag />
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.createdBy")}
              >
                <UserTag user={ticketData?.ticket?.get("user")} />
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.createdAt")}
              >
                {dayjs(ticketData.ticket.get("createdAt")).format(
                  "DD.MM.YYYY @ HH:mm:ss"
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:ticket.description.items.updatedAt")}
                span={2}
              >
                {dayjs(ticketData.ticket.get("updatedAt")).format(
                  "DD.MM.YYYY @ HH:mm:ss"
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("maintenance:general.ticket.material")}
                span={4}
              >
                <Flex vertical>
                  {ticketData?.material
                    ?.get("itemList")
                    ?.map((item, index) => (
                      <ItemLabel
                        key={item.id.toString() + index}
                        item={item.id.toString()}
                        quantity={item.quantity}
                      />
                    ))}
                </Flex>
              </Descriptions.Item>
            </Descriptions>
            <TicketMetaDescription />
          </FixedDescription>
        </Flex>

        <TicketSaveModal
          open={updateModalVisible}
          onClose={onCancelUpdate}
          onSaveSuccess={onOkUpdate}
          initialValues={initialValues}
        />
        <TicketDeleteModal
          open={deleteModalVisible}
          onCancel={onCancelDelete}
          onOk={onOkDelete}
        />

        <AssignUserModal
          open={assignModalVisible}
          onCancel={onCancelAssign}
          onOk={onOkAssign}
        />

        <ManualModal
          sources={[ticketData?.source]}
          open={manualModalVisible}
          onClose={() => {
            setManualModalVisible(false);
          }}
        />
      </div>
    </>
  );
});
